import { Button, InputNumber, Popover, Space, Tag, Typography } from "antd";
import { observer } from "mobx-react-lite";
import React, { useState } from "react";
import type { ResolvedPrice, ScenarioTag } from "../../types";

type TagPriceEntry = { tagId: string; [key: string]: any };

interface Props {
  tagPrices: TagPriceEntry[];
  allTags: ScenarioTag[];
  priceField: string;
  resolved: ResolvedPrice;
  onChange: (updated: TagPriceEntry[]) => void;
  label?: string;
}

export const TagPriceEditor = observer(({ tagPrices, allTags, priceField, resolved, onChange, label = "€" }: Props) => {
  const [open, setOpen] = useState(false);

  function setPrice(tagId: string, value: number | null) {
    const existing = tagPrices.find((tp) => tp.tagId === tagId);
    if (existing) {
      onChange(tagPrices.map((tp) => tp.tagId === tagId ? { ...tp, [priceField]: value ?? 0 } : tp));
    } else {
      onChange([...tagPrices, { tagId, [priceField]: value ?? 0 }]);
    }
  }

  const sortedTags = [...allTags].sort((a, b) => a.order - b.order);

  const content = (
    <Space direction="vertical" size={4}>
      {sortedTags.map((tag) => {
        const entry = tagPrices.find((tp) => tp.tagId === tag.id);
        return (
          <Space key={tag.id} size={4}>
            <Tag color={tag.color} style={{ minWidth: 80, textAlign: "center" }}>{tag.name}</Tag>
            <InputNumber
              size="small"
              min={0}
              step={0.01}
              value={entry ? entry[priceField] : undefined}
              placeholder="—"
              onChange={(v) => setPrice(tag.id, v)}
              style={{ width: 90 }}
            />
            <Typography.Text type="secondary" style={{ fontSize: 11 }}>€</Typography.Text>
          </Space>
        );
      })}
    </Space>
  );

  // Determine tag chip color for the resolved value
  const resolvedTag = allTags.find((t) => t.id === resolved.resolvedTagId);
  const chipColor = resolvedTag?.color ?? "#d9d9d9";
  const displayValue = resolved.isEmpty ? "—" : `${resolved.value.toFixed(2)} €`;

  return (
    <Popover
      content={content}
      title="Preise nach Szenario"
      trigger="click"
      open={open}
      onOpenChange={setOpen}
    >
      <Button
        size="small"
        style={{
          borderColor: chipColor,
          color: resolved.isEmpty ? "#bbb" : "inherit",
          background: resolved.isFallback ? `${chipColor}22` : undefined,
          minWidth: 80,
        }}
        title={resolved.isFallback ? `Fallback von Szenario: ${resolvedTag?.name}` : undefined}
      >
        {displayValue}
        {resolved.isFallback && <span style={{ fontSize: 9, marginLeft: 3, opacity: 0.7 }}>~</span>}
      </Button>
    </Popover>
  );
});
